import { Handshake, ListTodo, CircleDollarSign } from "lucide-react";
import { Topbar } from "../../components/layout/Topbar";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useThemeStore } from "../../store/theme.store";
import { BrandLogo } from "../../components/common/BrandLogo";

export default function LawSignaDashboard() {
  const { t } = useTranslation(["landing"]);
  const navigate = useNavigate();
  const { theme } = useThemeStore();

  const isTeal = theme === "teal" || theme === "ocean";

  const modules = [
    {
      id: "contracts",
      title: t("modules.contracts", { defaultValue: "Smart Drafting" }),
      icon: Handshake,
      active: false,
    },
    {
      id: "obligations",
      title: t("modules.obligations", { defaultValue: "Obligation Tracker" }),
      icon: ListTodo,
      active: false,
    },
    {
      id: "receivables",
      title: t("modules.receivables", { defaultValue: "Receivables Recovery" }),
      icon: CircleDollarSign,
      active: false,
    },
  ];

  return (
    <div
      className="min-x-screen min-h-screen transition-all duration-300 relative"
      style={{
        backgroundColor: "rgb(var(--bg))",
        color: "rgb(var(--text-primary))",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <header className="relative z-50">
        <Topbar />
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12 flex flex-col items-center justify-center text-center relative z-0">
        {/* Full High-Resolution Logo */}
        <div className="flex justify-center mb-8 w-full">
          <BrandLogo
            alt={t("logoAlt", { defaultValue: "Pactline Logo" })}
            className="h-24 sm:h-34 md:h-40 w-auto object-contain drop-shadow-sm"
          />
        </div>

        <h2 className="text-xl sm:text-2xl font-semibold mb-16 tracking-wide max-w-3xl leading-relaxed transition-colors duration-300 rgb(var(--text-secondary))">
          {t("tagline", {
            defaultValue:
              "Draft Smarter | Track Clearly | Collect Faster",
          })}
        </h2>

        {/* Modules Grid - Increased Box Sizes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 w-full max-w-5xl px-4 mt-2">
          {modules.map((mod) => {
            const IconComponent = mod.icon;
            return (
              <div
                key={mod.id}
                className="flex flex-col items-center group cursor-pointer"
                onClick={() => navigate("/")}
              >
                <div
                  className={`w-52 h-52 sm:w-56 sm:h-56 rounded-[32px] flex items-center justify-center mb-6 transition-all duration-300 transform group-hover:-translate-y-2 group-hover:shadow-2xl border border-[rgb(var(--border))] ${
                    mod.active
                      ? "ring-2 ring-[rgb(var(--text-accent))] ring-offset-4 ring-offset-[rgb(var(--bg))]"
                      : ""
                  }`}
                  style={{
                    background: isTeal
                      ? `linear-gradient(135deg, rgb(var(--chart-bar-primary)), rgb(var(--chart-line-primary)))`
                      : `linear-gradient(135deg, rgba(var(--chart-line-primary), 0.95), rgba(var(--chart-line-secondary), 0.95))`,
                    boxShadow: isTeal
                      ? "0 20px 40px -15px rgba(67, 197, 215, 0.4)"
                      : "0 20px 40px -15px rgba(0, 0, 0, 0.3)",
                  }}
                >
                  <IconComponent
                    className="w-28 h-28 stroke-[1.2] transition-transform duration-300 group-hover:scale-110"
                    style={{ color: isTeal ? "#ffffff" : "inherit" }}
                  />
                </div>

                <div className="mt-2">
                  <h3
                    className="text-xl font-semibold tracking-wide transition-colors duration-200"
                    style={{
                      color: mod.active
                        ? "rgb(var(--text-accent))"
                        : "rgb(var(--text-secondary))",
                    }}
                  >
                    {mod.title}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
